import React, { useContext, useMemo, useState } from "react";
import { Popover, message } from "antd";
import { CopyOutlined, ShareAltOutlined } from "@ant-design/icons";
import { Share } from "../Icons/Icons";
import { GlobalContext } from "../globalContext/GlobalContext";
import "./ShareLink.scss";

const copyShareUrl = async (shareUrl) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(shareUrl);
    return;
  }

  const textArea = document.createElement("textarea");
  textArea.value = shareUrl;
  textArea.setAttribute("readonly", "");
  textArea.style.position = "absolute";
  textArea.style.left = "-9999px";
  document.body.appendChild(textArea);
  textArea.select();
  document.execCommand("copy");
  document.body.removeChild(textArea);
};

const ShareLink = ({
  shareUrl,
  variant = "auto",
  placement = "bottomRight",
  children,
  triggerClassName = "share-trigger",
}) => {
  const { globalState } = useContext(GlobalContext) || {};
  const isMobile =
    variant === "mobile" || (variant === "auto" && Boolean(globalState?.isMobile));
  const [open, setOpen] = useState(false);

  const supportsNativeShare = useMemo(
    () => typeof navigator !== "undefined" && typeof navigator.share === "function",
    []
  );

  const handleCopy = async (e) => {
    e?.stopPropagation();

    try {
      await copyShareUrl(shareUrl);
      message.success("Link copied successfully");
      setOpen(false);
    } catch (err) {
      message.error("Failed to copy link");
    }
  };

  const handleNativeShare = async (e) => {
    e?.stopPropagation();

    if (!supportsNativeShare) {
      await handleCopy(e);
      return;
    }

    try {
      await navigator.share({
        title: document.title || "Share",
        url: shareUrl,
      });
      setOpen(false);
    } catch (err) {
      if (err?.name === "AbortError") {
        return;
      }
      await handleCopy(e);
    }
  };

  const content = (
    <div className="share-popup">
      <div className="share-actions">
        <button
          type="button"
          className="share-action-btn share-action-btn--primary"
          onClick={handleNativeShare}
        >
          <ShareAltOutlined />
          Share
        </button>
        <button type="button" className="share-action-btn" onClick={handleCopy}>
          <CopyOutlined />
          Copy Link
        </button>
      </div>
    </div>
  );

  return (
    <Popover
      content={content}
      trigger="click"
      placement={placement}
      overlayClassName={`share-popover${isMobile ? " share-popover--mobile" : ""}`}
      open={open}
      onOpenChange={setOpen}
    >
      <div
        className={triggerClassName}
        role="button"
        aria-label="Share site"
        tabIndex={0}
        onClick={(e) => e.stopPropagation()}
      >
        {children || <Share />}
      </div>
    </Popover>
  );
};

export default ShareLink;
